<script setup>
/**
 * The one centred search for lessons and Mystics (lesson-redo DESIGN.md C2).
 *
 * Resting: a generous field (640 wide at most, 60px tall, 54 on a phone,
 * radius 30, a gold hairline), labelled "Search lessons and Mystics". Its
 * placeholder is an overlay that fades: Try “…”, cycling every 3.2s through
 * recent public lesson questions; it stops while focused or holding text, and
 * under reduced motion shows the first only.
 *
 * Focused and empty: a panel drops from the field, text lines only (inline
 * text, never pills): Your recent searches (on this device only, each with ×,
 * and Clear), People are asking about (hidden when there are none), and the
 * Topics as one line. ↓/↑ move through the lines; Esc closes; a focus or press
 * outside closes it.
 *
 * v-model is the query. Emits `submit` (Enter: the phrase is kept in the
 * recent searches) and `pick` (a line or a topic tapped: searched at once).
 *
 * `panelId` names the panel (two fields on one page, community-feed-invite
 * §3.2); `drop="up"` opens it above the field (the feed's bottom).
 */
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import {
  SEARCH_LABEL, PLACEHOLDER_EVERY, TOPICS, placeholderParts, readRecent, rememberSearch, forgetSearch, clearRecent,
} from '@/utils/community-lessons'

const props = defineProps({
  modelValue: { type: String, default: '' },
  placeholders: { type: Array, default: () => [] },
  asking: { type: Array, default: () => [] },
  // Two fields on one page (community-feed-invite §3.2): each panel its own id.
  panelId: { type: String, default: 'lesson-search-panel' },
  // 'up' at the feed's bottom: the panel opens above the field, clear of a phone's keyboard.
  drop: { type: String, default: 'down' },
})
const emit = defineEmits(['update:modelValue', 'submit', 'pick'])

const root = ref(null)
const input = ref(null)
const focused = ref(false)
const panel = ref(false)
const recent = ref(readRecent())
const value = computed({ get: () => props.modelValue, set: (v) => emit('update:modelValue', v) })
const empty = computed(() => !String(props.modelValue || '').trim())
const open = computed(() => panel.value && empty.value)
const asks = computed(() => (props.asking || []).map((x) => String(x || '').trim()).filter(Boolean).slice(0, 4))

// ── The placeholder: Try “…”, cycling, fading ──
const phI = ref(0)
const phOut = ref(false)
const phList = computed(() => (props.placeholders || []).map((x) => String(x || '').trim()).filter(Boolean))
const phParts = computed(() => placeholderParts(phList.value.length ? phList.value[phI.value % phList.value.length] : ''))
const reduced = () => typeof window !== 'undefined' && !!(window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches)
let phTimer = null
let phSwap = null
function tick() {
  if (focused.value || !empty.value || phList.value.length < 2) return
  phOut.value = true
  phSwap = setTimeout(() => { phI.value = (phI.value + 1) % phList.value.length; phOut.value = false }, 350)
}

function openPanel() { recent.value = readRecent(); panel.value = true }
function closePanel() { panel.value = false }
function onFocus() { focused.value = true; openPanel() }
function onBlur() { focused.value = false }
function submit() {
  const q = String(props.modelValue || '').trim()
  if (q.length >= 2) recent.value = rememberSearch(q)
  closePanel()
  if (input.value) input.value.blur()
  emit('submit', q)
}
function pick(q) {
  value.value = q
  closePanel()
  emit('pick', q)
}
function remove(q) { recent.value = forgetSearch(q); focusInput() }
function clearAll() { recent.value = clearRecent(); focusInput() }
function clearQuery() { value.value = ''; focusInput() }
function focusInput() { if (input.value) { try { input.value.focus({ preventScroll: true }) } catch (e) { input.value.focus() } } }

function rows() { return root.value ? [...root.value.querySelectorAll('.sx-panel .sx-row, .sx-panel .sx-topic')] : [] }
function onKey(e) {
  if (e.key === 'Escape') {
    e.stopPropagation()
    if (!empty.value) { value.value = ''; return }
    closePanel()
    if (input.value) input.value.blur()
    return
  }
  if (e.key !== 'ArrowDown' && e.key !== 'ArrowUp') return
  const list = rows()
  if (!open.value || !list.length) return
  const k = list.indexOf(document.activeElement)
  e.preventDefault()
  if (e.key === 'ArrowDown') (list[k + 1] || list[k] || list[0]).focus()
  else if (k <= 0) focusInput()
  else list[k - 1].focus()
}
// A focus or press outside closes the panel.
function outside(e) { if (root.value && e.target && !root.value.contains(e.target)) closePanel() }
onMounted(() => {
  document.addEventListener('focusin', outside)
  document.addEventListener('pointerdown', outside)
  if (!reduced()) phTimer = setInterval(tick, PLACEHOLDER_EVERY)
})
onBeforeUnmount(() => {
  document.removeEventListener('focusin', outside)
  document.removeEventListener('pointerdown', outside)
  if (phTimer) clearInterval(phTimer)
  if (phSwap) clearTimeout(phSwap)
})
watch(() => props.modelValue, (v) => { if (String(v || '').trim() && panel.value) closePanel() })
defineExpose({ focus: focusInput, closePanel })
</script>

<template>
  <form ref="root" class="sx" :class="{ open, up: drop === 'up' }" role="search" data-testid="lesson-search" @submit.prevent="submit" @keydown="onKey">
    <div class="sx-field">
      <svg class="sx-ic" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" aria-hidden="true">
        <circle cx="11" cy="11" r="7" /><path d="M20 20l-3.5-3.5" />
      </svg>
      <input ref="input" v-model="value" class="sx-in" type="search" enterkeyhint="search" autocomplete="off" :aria-label="SEARCH_LABEL"
             :aria-controls="panelId" :aria-expanded="open ? 'true' : 'false'" data-testid="lessons-search"
             @focus="onFocus" @blur="onBlur" @click="openPanel" />
      <span v-show="empty" class="sx-ph" :class="{ out: phOut }" aria-hidden="true" data-testid="lessons-search-placeholder"><span class="sx-ph-pre">{{ phParts.pre }}</span><span class="sx-ph-q">{{ phParts.q }}</span><span class="sx-ph-post">{{ phParts.post }}</span></span>
      <button v-if="!empty" type="button" class="sx-x" aria-label="Clear the search" data-testid="lessons-search-clear" @click="clearQuery">×</button>
    </div>
    <div v-if="open" :id="panelId" class="sx-panel" data-testid="lesson-search-panel">
      <template v-if="recent.length">
        <p class="sx-h">Your recent searches <button type="button" class="sx-clear" data-testid="recent-clear" @click="clearAll">Clear</button></p>
        <div v-for="r in recent" :key="`r-${r}`" class="sx-line">
          <button type="button" class="sx-row" data-testid="recent-search" @click="pick(r)">
            <span class="i" aria-hidden="true">↺</span><span class="t">{{ r }}</span>
          </button>
          <button type="button" class="rm" :aria-label="`Remove ${r}`" data-testid="recent-remove" @click="remove(r)">×</button>
        </div>
      </template>
      <template v-if="asks.length">
        <p class="sx-h">People are asking about</p>
        <button v-for="a in asks" :key="`a-${a}`" type="button" class="sx-row" data-testid="asking-search" @click="pick(a)">
          <span class="i" aria-hidden="true">⌕</span><span class="t">{{ a }}</span>
        </button>
      </template>
      <p class="sx-h">Topics</p>
      <p class="sx-topics" data-testid="search-topics"><template v-for="(t, i) in TOPICS" :key="t.key"><template v-if="i"> · </template><button
        type="button" class="sx-topic" @click="pick(t.label)">{{ t.label }}</button></template></p>
    </div>
  </form>
</template>

<style scoped>
/* Under the Community's tab strip (z 5) as it scrolls by; over the lessons below (walk B9). */
.sx { position: relative; max-width: 640px; margin: 24px auto 0; text-align: left; z-index: 4; font-family: Inter, system-ui, -apple-system, 'Segoe UI', sans-serif; }
.sx *, .sx *::before, .sx *::after { box-sizing: border-box; }
.sx-field {
  position: relative; display: flex; align-items: center; gap: 10px; height: 60px; padding: 0 8px 0 22px; border-radius: 30px;
  background: rgba(255, 255, 255, .05); border: 1px solid rgba(231, 201, 131, .45); box-shadow: 0 18px 40px -24px #000;
  transition: border-color .2s, background .2s, box-shadow .2s;
}
.sx-field:focus-within { border-color: #e7c983; background: rgba(255, 255, 255, .08); box-shadow: 0 0 0 4px rgba(231, 201, 131, .12), 0 18px 40px -24px #000; }
.sx.open .sx-field { border-radius: 30px 30px 0 0; border-bottom-color: rgba(231, 201, 131, .18); }
.sx-ic { width: 20px; height: 20px; color: #e7c983; flex: none; }
.sx-in { flex: 1; min-width: 0; height: 100%; border: 0; outline: none; background: none; color: #fff; font: 500 17px Inter, system-ui, sans-serif; -webkit-appearance: none; appearance: none; }
.sx-in::-webkit-search-cancel-button { display: none; }
.sx-ph { position: absolute; left: 54px; right: 60px; top: 50%; transform: translateY(-50%); pointer-events: none; color: #a996c9; font: 500 17px Inter, system-ui, sans-serif;
  display: flex; white-space: nowrap; overflow: hidden; transition: opacity .35s ease, transform .35s ease; }
/* Only the question is cut: the ellipsis falls inside the closing quote (B7). */
.sx-ph-pre, .sx-ph-post { flex: none; }
.sx-ph-q { min-width: 0; overflow: hidden; text-overflow: ellipsis; }
.sx-ph.out { opacity: 0; transform: translateY(-30%); }
.sx-x { width: 44px; height: 44px; border: 0; border-radius: 50%; background: none; color: #c9b8e6; font: 400 24px/1 Inter, system-ui, sans-serif; cursor: pointer; flex: none; }
.sx-x:hover { color: #fff; }
.sx-x:focus-visible { outline: 2px solid #e7c983; outline-offset: -4px; }
/* The focused, empty field: text lines, no pills. */
.sx-panel { position: absolute; left: 0; right: 0; top: 100%; padding: 8px 0 12px; border-radius: 0 0 24px 24px; background: #1d0f36;
  border: 1px solid #e7c983; border-top: 0; box-shadow: 0 30px 60px -20px #000; animation: sx-drop .18s ease-out; }
.sx-h { display: flex; justify-content: space-between; align-items: baseline; margin: 10px 22px 2px; font: 600 9.5px 'Letter Display', 'Cinzel', Georgia, serif;
  letter-spacing: .2em; text-transform: uppercase; color: #cdb98a; }
.sx-clear { border: 0; background: none; color: #a996c9; font: 500 12px Inter, system-ui, sans-serif; cursor: pointer; letter-spacing: 0; text-transform: none; padding: 6px 0; min-height: 32px; }
.sx-clear:hover { color: #fff; }
.sx-line { display: flex; align-items: center; }
.sx-row { display: flex; align-items: center; gap: 12px; flex: 1; width: 100%; min-width: 0; min-height: 44px; padding: 0 22px; border: 0; background: none; cursor: pointer;
  text-align: left; font: 500 16px/1.3 Inter, system-ui, sans-serif; color: #ece2fb; }
.sx-line .sx-row { padding-right: 4px; }
.sx-row:hover, .sx-row:focus-visible, .sx-topic:focus-visible { background: rgba(231, 201, 131, .09); outline: none; }
.sx-row .i { width: 18px; color: #8e7fae; flex: none; text-align: center; }
.sx-row .t { flex: 1; min-width: 0; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.rm { flex: none; width: 44px; height: 44px; margin-right: 10px; border: 0; background: none; cursor: pointer; color: #8e7fae; font-size: 18px; }
.rm:hover { color: #fff; }
.rm:focus-visible { outline: 2px solid #e7c983; outline-offset: -6px; border-radius: 50%; }
.sx-topics { margin: 2px 22px 0; font: 500 15px/2 Inter, system-ui, sans-serif; color: #8e7fae; }
.sx-topic { border: 0; background: none; padding: 0; cursor: pointer; font: inherit; color: #ece2fb; text-decoration: underline;
  text-decoration-color: rgba(231, 201, 131, .35); text-underline-offset: 4px; border-radius: 3px; }
.sx-topic:hover { color: #ffe9b8; text-decoration-color: #e7c983; }
@keyframes sx-drop { from { opacity: 0; transform: translateY(-6px); } }
/* The feed's bottom: the panel rises above the field (§3.2). */
.sx.up.open .sx-field { border-radius: 0 0 30px 30px; border-bottom-color: #e7c983; border-top-color: rgba(231, 201, 131, .18); }
.sx.up .sx-panel { top: auto; bottom: 100%; border-radius: 24px 24px 0 0; border-top: 1px solid #e7c983; border-bottom: 0; padding: 12px 0 8px;
  box-shadow: 0 -30px 60px -20px #000; animation-name: sx-rise; }
@keyframes sx-rise { from { opacity: 0; transform: translateY(6px); } }
@media (max-width: 767px) {
  .sx-field { height: 54px; padding-left: 18px; }
  .sx-ph { left: 48px; font-size: 16px; }
  .sx-in { font-size: 16px; }
}
@media (prefers-reduced-motion: reduce) { .sx-panel { animation: none; } .sx-ph, .sx-field { transition: none; } }
</style>
